/**
 * Les calculs de date de la famille « temps » — PURS, en `Intl` et `Date` natifs, zéro dépendance.
 *
 * Deux sortes de temps, qu'on ne mélange jamais :
 *   - un INSTANT (`number`, millisecondes epoch) : un moment absolu, le même partout sur Terre ;
 *   - un JOUR (`Jour`) ou une heure MURALE (`Mural`) : ce qu'on lit sur un calendrier ou une
 *     horloge accrochée au mur, DANS un fuseau. Ce n'est un instant qu'une fois son fuseau connu.
 *
 * L'équipe vit à l'heure de Paris, Kayla en UTC−3 : un écran qui passe d'un instant à un jour
 * ou à une heure le fait TOUJOURS par un fuseau nommé (`jourDansFuseau`, `partiesDansFuseau`),
 * jamais par l'horloge du poste qui regarde. 🩸 Le cockpit l'a payé (`shared/shiftTime.ts`,
 * `FUSEAU_DES_SHIFTS`) : un poste réglé sur UTC lisait 06:30 quand Discord annonçait 08h30.
 *
 * Bascules d'heure : l'offset se lit à l'instant considéré (`formatToParts`), et un jour avance
 * par l'arithmétique des dates UTC, jamais par paquets de 24 h.
 */

/** Un jour du calendrier — sans heure ni fuseau. `mois` de 1 (janvier) à 12. */
export interface Jour {
  annee: number;
  mois: number;
  jour: number;
}

/** Un mois du calendrier. */
export interface Mois {
  annee: number;
  mois: number;
}

/** Une heure murale : un jour, une heure, une minute (et une seconde), lus dans un fuseau. */
export interface Mural extends Jour {
  heure: number;
  minute: number;
  seconde?: number;
}

/** 1 = lundi … 7 = dimanche (la semaine française). */
export type JourDeSemaine = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Les sept jours, lundi d'abord. */
export const JOURS_DE_SEMAINE: readonly JourDeSemaine[] = [1, 2, 3, 4, 5, 6, 7];

export const MINUTES_PAR_JOUR = 24 * 60;
const MS_PAR_JOUR = 24 * 60 * 60 * 1000;

const deux = (n: number) => String(n).padStart(2, "0");

/* ── Les jours ──────────────────────────────────────────────────────────── */

/** L'instant UTC de midi de ce jour : la sonde qui ne tombe jamais dans un trou d'heure d'été. */
function midiUtc(j: Jour): number {
  return Date.UTC(j.annee, j.mois - 1, j.jour, 12);
}

function depuisUtc(ms: number): Jour {
  const d = new Date(ms);
  return { annee: d.getUTCFullYear(), mois: d.getUTCMonth() + 1, jour: d.getUTCDate() };
}

/** « 2026-11-01 » — la clé d'un jour, la valeur d'un `<input type="date">`. */
export function cleDuJour(j: Jour): string {
  return `${String(j.annee).padStart(4, "0")}-${deux(j.mois)}-${deux(j.jour)}`;
}

/** Le nombre de jours d'un mois. */
export function joursDansLeMois(annee: number, mois: number): number {
  return new Date(Date.UTC(annee, mois, 0)).getUTCDate();
}

/** `true` si ce jour existe (pas de 31 avril). */
export function jourValide(j: Jour): boolean {
  return (
    Number.isInteger(j.annee) &&
    Number.isInteger(j.mois) &&
    Number.isInteger(j.jour) &&
    j.mois >= 1 &&
    j.mois <= 12 &&
    j.jour >= 1 &&
    j.jour <= joursDansLeMois(j.annee, j.mois)
  );
}

/** « 2026-11-01 » → le jour ; `null` si la clé est mal formée ou que le jour n'existe pas. */
export function jourDeCle(cle: string | null | undefined): Jour | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((cle ?? "").trim());
  if (!m) return null;
  const j = { annee: Number(m[1]), mois: Number(m[2]), jour: Number(m[3]) };
  return jourValide(j) ? j : null;
}

/** Le jour `n` jours plus loin (négatif : plus tôt). */
export function ajouterJours(j: Jour, n: number): Jour {
  return depuisUtc(midiUtc(j) + n * MS_PAR_JOUR);
}

/** Combien de jours de `a` à `b` (positif si `b` est après). */
export function ecartEnJours(a: Jour, b: Jour): number {
  return Math.round((midiUtc(b) - midiUtc(a)) / MS_PAR_JOUR);
}

/** −1, 0 ou 1 : l'ordre de deux jours. */
export function comparerJours(a: Jour, b: Jour): number {
  return Math.sign(ecartEnJours(b, a));
}

export function memeJour(a: Jour | null | undefined, b: Jour | null | undefined): boolean {
  return !!a && !!b && a.annee === b.annee && a.mois === b.mois && a.jour === b.jour;
}

/** `true` si `j` est entre `du` et `au`, bornes comprises (dans n'importe quel ordre). */
export function dansLaPlage(j: Jour, du: Jour | null | undefined, au: Jour | null | undefined): boolean {
  if (!du || !au) return false;
  const [a, b] = comparerJours(du, au) <= 0 ? [du, au] : [au, du];
  return comparerJours(j, a) >= 0 && comparerJours(j, b) <= 0;
}

/** Le jour de la semaine d'un jour du calendrier — 1 = lundi … 7 = dimanche. */
export function jourDeLaSemaine(j: Jour): JourDeSemaine {
  const d = new Date(midiUtc(j)).getUTCDay(); // 0 = dimanche
  return (d === 0 ? 7 : d) as JourDeSemaine;
}

/** Le lundi de la semaine de ce jour. */
export function lundiDe(j: Jour): Jour {
  return ajouterJours(j, 1 - jourDeLaSemaine(j));
}

/** Les sept jours de la semaine de `j`, du lundi au dimanche. */
export function semaineDe(j: Jour): Jour[] {
  const lundi = lundiDe(j);
  return JOURS_DE_SEMAINE.map((d) => ajouterJours(lundi, d - 1));
}

/** Le mois `n` mois plus loin. */
export function decalerMois(m: Mois, n: number): Mois {
  const index = m.annee * 12 + (m.mois - 1) + n;
  return { annee: Math.floor(index / 12), mois: (((index % 12) + 12) % 12) + 1 };
}

/** Une case de la grille d'un mois. */
export interface CaseDuMois {
  jour: Jour;
  cle: string;
  /** La case appartient au mois montré (sinon : un jour du mois d'avant ou d'après). */
  dansLeMois: boolean;
}

/**
 * Les 42 cases d'un mois — six semaines, lundi d'abord. Toujours six : un calendrier qui change
 * de hauteur d'un mois à l'autre fait sauter ce qu'il y a dessous.
 */
export function grilleDuMois(m: Mois): CaseDuMois[] {
  const premier = lundiDe({ annee: m.annee, mois: m.mois, jour: 1 });
  return Array.from({ length: 42 }, (_, i) => {
    const jour = ajouterJours(premier, i);
    return { jour, cle: cleDuJour(jour), dansLeMois: jour.mois === m.mois && jour.annee === m.annee };
  });
}

/* ── Les fuseaux ────────────────────────────────────────────────────────── */

const formateurs = new Map<string, Intl.DateTimeFormat>();

function formateurDesParties(fuseau: string): Intl.DateTimeFormat {
  let f = formateurs.get(fuseau);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: fuseau,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formateurs.set(fuseau, f);
  }
  return f;
}

/** `true` si le fuseau IANA est connu du navigateur. */
export function fuseauValide(fuseau: string | null | undefined): boolean {
  if (!fuseau) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: fuseau });
    return true;
  } catch {
    return false;
  }
}

/** Le fuseau de l'appareil (« America/Asuncion ») ; « UTC » s'il ne le dit pas. */
export function fuseauDeLAppareil(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Ce qu'affiche une horloge murale de `fuseau` à l'instant `ms`. */
export function partiesDansFuseau(ms: number, fuseau: string): Required<Mural> {
  const p: Record<string, number> = {};
  for (const part of formateurDesParties(fuseau).formatToParts(new Date(ms))) {
    if (part.type !== "literal") p[part.type] = Number(part.value);
  }
  return {
    annee: p.year,
    mois: p.month,
    jour: p.day,
    heure: p.hour === 24 ? 0 : p.hour,
    minute: p.minute,
    seconde: p.second,
  };
}

/** L'avance de `fuseau` sur UTC à l'instant `ms`, en minutes (Paris l'été : 120 ; Asunción : −180). */
export function decalageDuFuseau(ms: number, fuseau: string): number {
  const p = partiesDansFuseau(ms, fuseau);
  const murUtc = Date.UTC(p.annee, p.mois - 1, p.jour, p.heure, p.minute, p.seconde);
  return Math.round((murUtc - Math.floor(ms / 1000) * 1000) / 60_000);
}

/**
 * L'instant où l'horloge murale de `fuseau` affiche `m`. Dans le trou d'une heure d'été (02:30 un
 * dimanche de mars à Paris), l'heure qui suit ; dans l'heure doublée d'octobre, la première.
 */
export function instantDepuisMural(m: Mural, fuseau: string): number {
  const mur = Date.UTC(m.annee, m.mois - 1, m.jour, m.heure, m.minute, m.seconde ?? 0);
  // Deux candidats : l'offset d'avant et celui d'après une éventuelle bascule (±12 h encadrent
  // toutes celles du monde). On garde ceux dont l'horloge affiche bien `m`, le plus tôt d'abord ;
  // aucun (le trou de mars) : l'offset d'avant, qui pousse l'heure au-delà du trou.
  const avant = mur - decalageDuFuseau(mur - 12 * 3_600_000, fuseau) * 60_000;
  const apres = mur - decalageDuFuseau(mur + 12 * 3_600_000, fuseau) * 60_000;
  const affiche = (ms: number) => {
    const p = partiesDansFuseau(ms, fuseau);
    return p.annee === m.annee && p.mois === m.mois && p.jour === m.jour && p.heure === m.heure && p.minute === m.minute;
  };
  const justes = [avant, apres].filter(affiche);
  return justes.length > 0 ? Math.min(...justes) : avant;
}

/** Le jour du calendrier que montre `fuseau` à l'instant `ms`. */
export function jourDansFuseau(ms: number, fuseau: string): Jour {
  const p = partiesDansFuseau(ms, fuseau);
  return { annee: p.annee, mois: p.mois, jour: p.jour };
}

/** Le jour de la semaine à l'instant `ms` dans `fuseau` — 1 = lundi. */
export function jourDeSemaineDansFuseau(ms: number, fuseau: string): JourDeSemaine {
  return jourDeLaSemaine(jourDansFuseau(ms, fuseau));
}

/** La minute du jour (0 à 1439) à l'instant `ms` dans `fuseau`. */
export function minuteDuJourDansFuseau(ms: number, fuseau: string): number {
  const p = partiesDansFuseau(ms, fuseau);
  return p.heure * 60 + p.minute;
}

/** L'instant où commence le jour `j` (minuit) dans `fuseau`. */
export function debutDuJour(j: Jour, fuseau: string): number {
  return instantDepuisMural({ ...j, heure: 0, minute: 0 }, fuseau);
}

/* ── Les heures murales en texte ────────────────────────────────────────── */

/** 570 → « 09:30 ». Au-delà d'une journée, on fait le tour du cadran (1500 → « 01:00 »). */
export function minutesEnHHMM(minutes: number): string {
  const m = ((Math.round(minutes) % MINUTES_PAR_JOUR) + MINUTES_PAR_JOUR) % MINUTES_PAR_JOUR;
  return `${deux(Math.floor(m / 60))}:${deux(m % 60)}`;
}

/**
 * Ce qu'on tape dans un champ d'heure → des minutes depuis minuit ; `null` si ce n'est pas une
 * heure. Tout ce qu'une main écrit : « 9 », « 09 », « 930 », « 0930 », « 9:30 », « 9h30 »,
 * « 9 h », « 21 h 15 », « 9.30 ».
 */
export function lireHeure(texte: string | null | undefined): number | null {
  const t = (texte ?? "").trim().toLowerCase().replace(/\s+/g, "");
  if (!t) return null;
  let h: number;
  let m: number;
  const separe = /^(\d{1,2})(?:[:h.](\d{1,2})?)?$/.exec(t);
  const colle = /^(\d{3,4})$/.exec(t);
  if (colle) {
    const n = colle[1].padStart(4, "0");
    h = Number(n.slice(0, 2));
    m = Number(n.slice(2));
  } else if (separe) {
    h = Number(separe[1]);
    m = separe[2] === undefined ? 0 : Number(separe[2].length === 1 ? `${separe[2]}0` : separe[2]);
  } else {
    return null;
  }
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
}

/** « 2026-11-01T09:30 » (la valeur d'un `datetime-local`) → un jour et une minute ; `null` sinon. */
export function lireDateHeure(valeur: string | null | undefined): { jour: Jour; minute: number } | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::\d{2})?$/.exec((valeur ?? "").trim());
  if (!m) return null;
  const jour = jourDeCle(m[1]);
  const minute = lireHeure(m[2]);
  return jour && minute !== null ? { jour, minute } : null;
}

/** Un jour et une minute → « 2026-11-01T09:30 ». */
export function cleDateHeure(jour: Jour, minute: number): string {
  return `${cleDuJour(jour)}T${minutesEnHHMM(minute)}`;
}

/* ── Les créneaux d'une journée ─────────────────────────────────────────── */

/** `true` si le créneau passe minuit (fin ≤ début : 21:00 → 03:00). */
export function passeMinuit(debut: number, fin: number): boolean {
  return fin <= debut;
}

/** La durée d'un créneau en minutes, minuit compris (21:00 → 03:00 = 360). */
export function dureeDuCreneau(debut: number, fin: number): number {
  return fin > debut ? fin - debut : MINUTES_PAR_JOUR - debut + fin;
}
